import React, { useState, useEffect } from 'react';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import '../css/AnalyticsDashboard.css';
import { 
  Bar, 
  BarChart, 
  CartesianGrid, 
  Cell, 
  Legend, 
  Line, 
  LineChart, 
  Pie, 
  PieChart, 
  ResponsiveContainer, 
  Tooltip, 
  XAxis, 
  YAxis 
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { groupBy } from 'lodash';




  const AnalyticsDashboard = () => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [dateRange, setDateRange] = useState('last30');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [customStartDate, setCustomStartDate] = useState(null);
const [customEndDate, setCustomEndDate] = useState(null);

  
    // Define fetchData outside of useEffect so it can be reused
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Calculate date range
        let startDate, endDate;
        const now = new Date();
        endDate = now.toISOString();
        
        switch (dateRange) {
          case 'last7':
            startDate = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
            break;
          case 'last30':
            startDate = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
            break;
          case 'last90':
            startDate = new Date(now - 90 * 24 * 60 * 60 * 1000).toISOString();
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              startDate = customStartDate.toISOString();
              endDate = customEndDate.toISOString();
            } else {
              return; // Skip fetching if dates aren't ready
            }
            break;
          default:
            startDate = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        }
        
        
        const response = await api.get('/api/analytics/dashboard-data', {
          params: { startDate, endDate }
        });
        
        setAnalyticsData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Failed to load analytics data');
        setLoading(false);
      }
    };
  
    const handleClearTestData = async () => {
      if (window.confirm('Are you sure you want to clear all analytics test data? This cannot be undone.')) {
        try {
          setLoading(true);
          await api.delete('/api/analytics/clear-test-data');
          // Refresh the data
          setAnalyticsData(null);
          // Wait a moment then fetch fresh data
          setTimeout(() => {
            fetchData(); // Now this will work
          }, 500);
          alert('Test data successfully cleared!');
        } catch (error) {
          console.error('Error clearing test data:', error);
          alert('Failed to clear test data: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    };

    const handleDownloadData = () => {
      const grouped = groupBy(analyticsData.dailyPageViews, (entry) => {
        const date = new Date(entry._id); // ← use "_id" not "date"
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      });
      
      // Format data for CSV
      const csvData = [
        // CSV Headers
        ['Page Path', 'Views'],
        // Page view data
        ...analyticsData.popularPages.map(page => [page._id, page.count]),
        // Empty line
        [],
        // Device data
        ['Device Type', 'Count'],
        ...analyticsData.deviceBreakdown.map(device => [device._id, device.count]),
        // Empty line
        [],
        // Language data
        ['Language', 'Count'],
        ...analyticsData.languageBreakdown.map(lang => [lang._id, lang.count])
      ];
      
      // Convert to CSV string
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Set up download
      link.setAttribute('href', url);
      link.setAttribute('download', `analytics_${dateRange}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    
    // Use fetchData in the useEffect
    useEffect(() => {
      if (dateRange === 'custom') {
        // Wait until both dates are selected before triggering fetch
        if (!customStartDate || !customEndDate) return;
      }
    
      fetchData();
    }, [dateRange, customStartDate, customEndDate]);
    
    const getMonthlyViews = () => {
      if (!analyticsData?.dailyPageViews) return [];
    
      const grouped = groupBy(analyticsData.dailyPageViews, (entry) => {
        const date = new Date(entry._id);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      });
    
      let result = Object.entries(grouped).map(([month, entries]) => ({
        month,
        views: entries.reduce((sum, e) => sum + e.count, 0)
      })).sort((a, b) => a.month.localeCompare(b.month));
    
      // ✅ limit to last 12 months for predefined ranges
      if (dateRange !== 'custom') {
        result = result.slice(-12);
      }
    
      return result;
    };
    
    
    
  
  // Format path names to be more readable
  const formatPagePath = (path) => {
    if (path === '/') return 'Home';
    if (path === '/home') return 'Home';
    if (path === '/faq') return 'FAQ';
    if (path === '/infonieuw') return 'Nieuwe bewoners';
    if (path === '/hhr') return 'Huishoudelijk reglement';
    if (path === '/contact') return 'Contact';
    if (path === '/beheer') return 'Beheerdersportaal';
    
    // Remove leading slash and capitalize
    return path.substring(1).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };


  
  // Render loading state
  if (loading) {
    return (
      <div className="beheer-wrapper">
        <BeheerdersportaalSidebar>
          <main className="dashboard">
            <h1>Website Analytics</h1>
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading analytics data...</p>
            </div>
          </main>
        </BeheerdersportaalSidebar>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="beheer-wrapper">
        <BeheerdersportaalSidebar>
          <main className="dashboard">
            <h1>Website Analytics</h1>
            <div className="error-message">{error}</div>
          </main>
        </BeheerdersportaalSidebar>
      </div>
    );
  }
  
  return (
    <div className="beheer-wrapper">
      <BeheerdersportaalSidebar>
        <main className="dashboard">
          <h1>Website Analytics</h1>
          
          {/* Date range selector */}
          <div className="date-range-selector">
  <button className={`date-button ${dateRange === 'last7' ? 'active' : ''}`} onClick={() => setDateRange('last7')}>Laatste 7 dagen</button>
  <button className={`date-button ${dateRange === 'last30' ? 'active' : ''}`} onClick={() => setDateRange('last30')}>Laatste 30 dagen</button>
  <button className={`date-button ${dateRange === 'last90' ? 'active' : ''}`} onClick={() => setDateRange('last90')}>Laatste 90 dagen</button>

  {/* Custom Date Picker */}
  <div className="custom-date-picker">
  <DatePicker
    className="datepicker-input"
    selected={customStartDate}
    onChange={(date) => {
      setCustomStartDate(date);
      setDateRange('custom');
    }}
    selectsStart
    startDate={customStartDate}
    endDate={customEndDate}
    placeholderText="Startdatum"
  />
  <DatePicker
    className="datepicker-input"
    selected={customEndDate}
    onChange={(date) => {
      setCustomEndDate(date);
      setDateRange('custom');
    }}
    selectsEnd
    startDate={customStartDate}
    endDate={customEndDate}
    minDate={customStartDate}
    placeholderText="Einddatum"
  />
</div>

</div>


          <div className="admin-actions">
  <button 
    className="download-button"
    onClick={handleDownloadData}
  >
    Download Data
  </button>
  <button 
    className="clear-data-button"
    onClick={handleClearTestData}
  >
    Clear Test Data
  </button>
</div>
          
          {analyticsData && (
            <div className="analytics-grid">
              {/* Overview stats */}
              <div className="overview-stats">
                <div className="stat-card">
                  <h3>Totaal Pageviews</h3>
                  <div className="stat-value">{analyticsData.totalPageViews}</div>
                </div>
                
                <div className="stat-card">
                  <h3>Unieke Bezoekers</h3>
                  <div className="stat-value">{analyticsData.uniqueVisitors}</div>
                </div>
                
                <div className="stat-card">
                  <h3>Pagina's per Bezoeker</h3>
                  <div className="stat-value">
                    {analyticsData.uniqueVisitors ? 
                      (analyticsData.totalPageViews / analyticsData.uniqueVisitors).toFixed(2) : 0}
                  </div>
                </div>
              </div>
              
              {/* Mobile vs Desktop chart */}
              <div className="chart-card">
                <h3>Apparaat Type</h3>
                <div className="chart-container2">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData.deviceBreakdown.map(item => ({
                          name: item._id === 'mobile' ? 'Mobiel' : 'Desktop',
                          value: item.count
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        label
                      >
                        {analyticsData.deviceBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry._id === 'mobile' ? '#82ca9d' : '#8884d8'} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>




              </div>
              
              {/* Popular pages chart */}
              <div className="chart-card">
                <h3>Meest Bezochte Pagina's</h3>
                <div className="chart-container2">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={analyticsData.popularPages.map(item => ({
                        name: formatPagePath(item._id),
                        views: item.count
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="views" fill="#34a853" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              {/* Language preference chart */}
              <div className="chart-card">
                <h3>Taalvoorkeuren</h3>
                <div className="chart-container2">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData.languageBreakdown.map(item => ({
                          name: item._id === 'nl' ? 'Nederlands' : 'Engels',
                          value: item.count
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        label
                      >
                        {analyticsData.languageBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry._id === 'nl' ? '#4285F4' : '#DB4437'} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart-card wide">
  <h3>Views per Maand</h3>
  <div className="chart-container2">
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={getMonthlyViews()}
        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" angle={-45} textAnchor="end" height={70} />
        <YAxis />
        <Tooltip />
        <Bar dataKey="views" fill="#1a73e8" />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>
              
              {/* Popular FAQs if data is available */}
              {analyticsData.popularFaqs && analyticsData.popularFaqs.length > 0 && (
                <div className="chart-card wide">
                  <h3>Meest Bekeken FAQ Items</h3>
                  <div className="faq-table-container">
                    <table className="faq-table">
                      <thead>
                        <tr>
                          <th>FAQ Item</th>
                          <th>Bekeken</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.popularFaqs.map((faq, index) => (
                          <tr key={index}>
                            <td>{faq._id}</td>
                            <td>{faq.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </BeheerdersportaalSidebar>
    </div>
  );
};

export default AnalyticsDashboard